import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { restoreSession, signOut } from "./lib/auth";
import { SignIn } from "./pages/SignIn";
import { Dashboard } from "./pages/Dashboard";
import { Explorer } from "./pages/Explorer";
import { Intake } from "./pages/Intake";
import { ItemDetail } from "./pages/ItemDetail";
import { Listings } from "./pages/Listings";
import { Sales } from "./pages/Sales";
import { Finance } from "./pages/Finance";
import { Reconcile } from "./pages/Reconcile";
import { Sync } from "./pages/Sync";
import { Settings } from "./pages/Settings";

const LINKS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: "/", label: "Inventory", end: true },
  { to: "/explorer", label: "Explorer" },
  { to: "/intake", label: "Add item" },
  { to: "/listings", label: "Listed" },
  { to: "/sales", label: "Sold" },
  { to: "/money", label: "Money" },
  { to: "/reconcile", label: "Tie out" },
  { to: "/sync", label: "Sync" },
  { to: "/settings", label: "Settings" },
];

/**
 * The menu. On a wide screen the links sit inline in the bar. On a phone the
 * same links live behind a menu button and drop down as one list under the
 * bar, so every page is a tap away — the sideways-scrolling row hid Settings
 * behind a swipe nobody found.
 */
function TopBar({ onSignOut }: { onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Navigating closes the menu; so does the Escape key.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className={`topbar ${open ? "menu-open" : ""}`}>
      <span className="topbar-brand">thrifty<span>business</span></span>
      <nav className="topbar-nav" id="site-menu" aria-label="Pages">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end}>{l.label}</NavLink>
        ))}
        <button className="quiet topbar-signout topbar-signout-menu" onClick={onSignOut}>
          Sign out
        </button>
      </nav>
      <button className="quiet topbar-signout topbar-signout-bar" onClick={onSignOut}>
        Sign out
      </button>
      <button
        type="button"
        className="quiet topbar-menu-btn"
        aria-expanded={open}
        aria-controls="site-menu"
        aria-label={open ? "Close menu" : "Open menu"}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="topbar-menu-icon" aria-hidden="true" />
        <span>{open ? "Close" : "Menu"}</span>
      </button>
      {open && <div className="topbar-scrim" onClick={() => setOpen(false)} aria-hidden="true" />}
    </header>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    restoreSession().then((ok) => { setAuthed(ok); setChecking(false); });
  }, []);

  if (checking) {
    return <div className="shell"><p className="hint" style={{ paddingTop: "18vh" }}>Loading</p></div>;
  }

  if (!authed) return <SignIn onSignedIn={() => setAuthed(true)} />;

  return (
    <BrowserRouter>
      <TopBar onSignOut={() => { signOut(); setAuthed(false); }} />

      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/explorer" element={<Explorer />} />
        <Route path="/intake" element={<Intake />} />
        <Route path="/items/:id" element={<ItemDetail />} />
        <Route path="/listings" element={<Listings />} />
        <Route path="/sales" element={<Sales />} />
        <Route path="/money" element={<Finance />} />
        <Route path="/reconcile" element={<Reconcile />} />
        <Route path="/sync" element={<Sync />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
